/**
 * useClickOutside Hook
 *
 * Detects clicks outside of a referenced element.
 * Useful for closing dropdowns, modals, and overlays.
 *
 * @example
 * ```tsx
 * function Dropdown() {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const dropdownRef = useRef<HTMLDivElement>(null);
 *
 *   useClickOutside(dropdownRef, () => {
 *     setIsOpen(false);
 *   });
 *
 *   return (
 *     <div ref={dropdownRef}>
 *       {isOpen && <DropdownContent />}
 *     </div>
 *   );
 * }
 * ```
 */

import { useEffect, useRef, type RefObject } from 'react';

/**
 * Hook that triggers a callback when clicking outside the referenced element.
 *
 * @param ref - Reference to the element to detect clicks outside of
 * @param handler - Callback function to run when clicking outside
 * @param enabled - Whether the hook is active (default: true)
 */
export function useClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: MouseEvent | TouchEvent) => void,
  enabled: boolean = true
): void {
  // Store handler in a ref to avoid re-running effect when handler changes
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: MouseEvent | TouchEvent) => {
      const el = ref.current;

      // Do nothing if clicking ref's element or its descendants
      if (!el || el.contains(event.target as Node)) {
        return;
      }

      handlerRef.current(event);
    };

    // Use mousedown for immediate feedback (before click completes)
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, enabled]);
}

/**
 * Hook that returns a ref and triggers callback on outside clicks.
 * Convenience wrapper that creates the ref for you.
 *
 * @param handler - Callback function to run when clicking outside
 * @param enabled - Whether the hook is active (default: true)
 * @returns Ref to attach to the element
 *
 * @example
 * ```tsx
 * function Dropdown() {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const containerRef = useClickOutsideRef<HTMLDivElement>(() => {
 *     setIsOpen(false);
 *   }, isOpen);
 *
 *   return (
 *     <div ref={containerRef}>
 *       {isOpen && <DropdownContent />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useClickOutsideRef<T extends HTMLElement = HTMLElement>(
  handler: (event: MouseEvent | TouchEvent) => void,
  enabled: boolean = true
): RefObject<T | null> {
  const ref = useRef<T>(null);
  useClickOutside(ref, handler, enabled);
  return ref;
}

export default useClickOutside;
